import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Checkbox,
  Radio,
} from "@mui/material";
import { LeagueChip } from "../../Components/LeagueChip/LeagueChip";
import { getLeagueChipStyle } from "../../utils/leagueChipStyles";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { fetchScraperDataV2 } from "../../services/scraper/scraperService";
import {
  ScraperMatchV2,
  ScraperPresetResponse,
} from "../../services/scraper/types";
import { getLeagueSortIndex } from "./utils/leagueOrder";

type DifficultyPreset = "easy" | "moderate" | "hard" | "custom";
type DifficultySort = "none" | "asc" | "desc";
type ModalStep = "selection" | "pleno";

const MAX_SELECTION = 15;
const difficultyRanking: Record<ScraperMatchV2["difficulty"], number> = {
  hard: 0,
  moderate: 1,
  easy: 2,
  unknown: 3,
};

const difficultyGradients: Record<
  ScraperMatchV2["difficulty"],
  { background: string; color: string }
> = {
  easy: {
    background: "linear-gradient(135deg, #43a047, #8bc34a)",
    color: "#ffffff",
  },
  moderate: {
    background: "linear-gradient(135deg, #f9a825, #ffca28)",
    color: "#2d1600",
  },
  hard: {
    background: "linear-gradient(135deg, #c62828, #ff7043)",
    color: "#ffffff",
  },
  unknown: {
    background: "linear-gradient(135deg, #607d8b, #90a4ae)",
    color: "#ffffff",
  },
};

const presetGradients: Record<
  Exclude<DifficultyPreset, "custom">,
  { background: string; hover: string }
> = {
  easy: {
    background: "linear-gradient(135deg, #43a047, #8bc34a)",
    hover: "linear-gradient(135deg, #2e7d32, #689f38)",
  },
  moderate: {
    background: "linear-gradient(135deg, #f9a825, #ffca28)",
    hover: "linear-gradient(135deg, #f57f17, #ffb300)",
  },
  hard: {
    background: "linear-gradient(135deg, #c62828, #ff7043)",
    hover: "linear-gradient(135deg, #8e0000, #f4511e)",
  },
};

const presetTextColors: Record<Exclude<DifficultyPreset, "custom">, string> = {
  easy: "#2e7d32",
  moderate: "#f57f17",
  hard: "#c62828",
};

// League chip styles are now imported from utils/leagueChipStyles

const primaryActionStyles = {
  background: "linear-gradient(135deg, #b8860b, #ffd54f)",
  color: "#3e2723",
  borderRadius: 999,
  fontWeight: 700,
  px: 3,
  // boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
  "&:hover": {
    background: "linear-gradient(135deg, #a07007, #ffca28)",
    color: "#3e2723",
  },
  "&.Mui-disabled": {
    color: "rgba(62,39,35,0.6)",
    background:
      "linear-gradient(135deg, rgba(184,134,11,0.3), rgba(255,213,79,0.3))",
  },
};

const secondaryActionStyles = {
  borderRadius: 999,
  fontWeight: 600,
  px: 2.5,
  color: "#4527a0",
  border: "2px solid #4527a0",
  "&:hover": {
    borderColor: "#311b92",
    color: "#311b92",
    backgroundColor: "rgba(69,39,160,0.05)",
  },
};

interface MatchAutoFillModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    matches: ScraperMatchV2[];
    plenoMatchId: string | null;
    preset: DifficultyPreset;
  }) => void;
}

export function MatchAutoFillModal({
  open,
  onClose,
  onConfirm,
}: MatchAutoFillModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    matches: ScraperMatchV2[];
    presets: ScraperPresetResponse;
    quotas: Record<string, number>;
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [plenoMatchId, setPlenoMatchId] = useState<string | null>(null);
  const [activePreset, setActivePreset] = useState<DifficultyPreset>("custom");
  const [selectionWarning, setSelectionWarning] = useState<string | null>(null);
  const [showUnselected, setShowUnselected] = useState(true);
  const [difficultySort, setDifficultySort] = useState<DifficultySort>("none");
  const [currentStep, setCurrentStep] = useState<ModalStep>("selection");
  const [lessThan15Dismissed, setLessThan15Dismissed] = useState(false);

  const presetLabels: Record<Exclude<DifficultyPreset, "custom">, string> = {
    easy: t("autoFillModal.easyPreset"),
    moderate: t("autoFillModal.moderatePreset"),
    hard: t("autoFillModal.hardPreset"),
  };

  const difficultyLabels: Record<ScraperMatchV2["difficulty"], string> = {
    easy: t("autoFillDifficulty.easy"),
    moderate: t("autoFillDifficulty.moderate"),
    hard: t("autoFillDifficulty.hard"),
    unknown: t("autoFillDifficulty.unknown"),
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    if (data) {
      return;
    }
    setLoading(true);
    fetchScraperDataV2()
      .then((response) => {
        setData(response);
        setSelectedIds([]);
        setPlenoMatchId(null);
        setActivePreset("custom");
        setCurrentStep("selection");
        setShowUnselected(true);
        setDifficultySort("none");
        setSelectionWarning(null);
        setLessThan15Dismissed(false);
      })
      .catch((err) => {
        console.error(err);
        setError(
          err?.message ??
            t("autoFillModal.fetchError")
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, data, t]);

  useEffect(() => {
    if (!open) {
      setCurrentStep("selection");
      setShowUnselected(true);
      setDifficultySort("none");
      setSelectionWarning(null);
      setLessThan15Dismissed(false);
      setActivePreset("custom");
      return;
    }
    if (!data) {
      return;
    }
    if (!plenoMatchId || !selectedIds.includes(plenoMatchId)) {
      setPlenoMatchId(selectedIds[selectedIds.length - 1] ?? null);
    }
  }, [selectedIds, plenoMatchId, open, data]);

  const matchMap = useMemo(() => {
    if (!data) return new Map<string, ScraperMatchV2>();
    return new Map(data.matches.map((match) => [match.matchId, match]));
  }, [data]);

  const selectedMatches = useMemo(() => {
    return selectedIds
      .map((id) => matchMap.get(id))
      .filter(Boolean) as ScraperMatchV2[];
  }, [selectedIds, matchMap]);

  const championsCount = selectedMatches.filter(
    (match) => match.isChampionsLeague
  ).length;
  const selectedCount = selectedIds.length;
  const isLessThanMaxSelection =
    selectedCount > 0 && selectedCount < MAX_SELECTION;
  const isMaxSelection = selectedCount === MAX_SELECTION;

  useEffect(() => {
    setLessThan15Dismissed(false);
  }, [selectedCount]);

  const handlePresetClick = (preset: Exclude<DifficultyPreset, "custom">) => {
    if (!data) return;
    const presetIds = data.presets[preset] ?? [];
    if (presetIds.length === 0) {
      return;
    }
    const filtered = presetIds.filter((id) => matchMap.has(id)).slice(0, 15);
    setSelectedIds(filtered);
    setActivePreset(preset);
    setSelectionWarning(null);
    setCurrentStep("selection");
    setShowUnselected(false);
  };

  const handleToggle = (matchId: string) => {
    if (!matchMap.has(matchId)) return;
    setActivePreset("custom");
    setSelectionWarning(null);
    setSelectedIds((prev) => {
      if (prev.includes(matchId)) {
        return prev.filter((id) => id !== matchId);
      }
      if (prev.length >= MAX_SELECTION) {
        setSelectionWarning(
          t("autoFillModal.limitWarning")
        );
        return prev;
      }
      return [...prev, matchId];
    });
  };

  const handleNext = () => {
    if (selectedIds.length === 0) {
      setSelectionWarning(
        t("autoFillModal.noMatchesSelected")
      );
      return;
    }
    setSelectionWarning(null);
    if (selectedIds.length < MAX_SELECTION) {
      setPlenoMatchId(null);
      handleConfirm();
      return;
    }
    setCurrentStep("pleno");
    setPlenoMatchId(
      selectedIds[selectedIds.length - 1] ?? plenoMatchId ?? null
    );
  };

  const handleConfirm = () => {
    if (!data) return;
    const selection = selectedMatches;
    if (selection.length === 0) {
      setSelectionWarning(
        t("autoFillModal.noMatchesSelected")
      );
      return;
    }
    const validPleno =
      plenoMatchId && selection.some((match) => match.matchId === plenoMatchId)
        ? plenoMatchId
        : null;
    onConfirm({
      matches: selection,
      plenoMatchId: validPleno,
      preset: activePreset,
    });
    onClose();
  };

  const renderDifficultyChip = (match: ScraperMatchV2) => (
    <Chip
      label={difficultyLabels[match.difficulty]}
      size="small"
      sx={{
        background: difficultyGradients[match.difficulty].background,
        color: difficultyGradients[match.difficulty].color,
        fontWeight: 600,
      }}
    />
  );

  const displayedMatches = useMemo(() => {
    if (!data) return [];
    let list = [...data.matches];
    if (!showUnselected) {
      list = list.filter((match) => selectedIds.includes(match.matchId));
    }
    list.sort((a, b) => {
      const leagueDiff =
        getLeagueSortIndex(a.leagueId) - getLeagueSortIndex(b.leagueId);
      if (leagueDiff !== 0) return leagueDiff;
      return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
    });
    if (difficultySort !== "none") {
      const multiplier = difficultySort === "asc" ? 1 : -1;
      list = [...list].sort(
        (a, b) =>
          multiplier *
          (difficultyRanking[a.difficulty] - difficultyRanking[b.difficulty])
      );
    }
    return list;
  }, [data, showUnselected, selectedIds, difficultySort]);

  const selectedOnly = useMemo(() => {
    return selectedIds
      .map((id) => matchMap.get(id))
      .filter(Boolean) as ScraperMatchV2[];
  }, [selectedIds, matchMap]);

  const renderSelectionStep = () => (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        <Typography fontWeight="bold">
          {t("autoFillModal.selectedCount", {
            count: selectedIds.length,
            max: MAX_SELECTION,
          })}
        </Typography>
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent={{ xs: "space-between", md: "center" }}
          sx={{ width: "100%" }}
          gap={1}
        >
          {(["easy", "moderate", "hard"] as const).map((preset) => {
            const isActive = activePreset === preset;
            return (
              <Button
                key={preset}
                variant={isActive ? "contained" : "outlined"}
                onClick={() => handlePresetClick(preset)}
                sx={{
                  borderRadius: 999,
                  // minWidth: 140,
                  border: isActive
                    ? "none"
                    : `2px solid ${presetTextColors[preset]}`,
                  color: isActive ? "#fff" : presetTextColors[preset],
                  background: isActive
                    ? presetGradients[preset].background
                    : "transparent",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  boxShadow: isActive ? "0 10px 24px rgba(0,0,0,0.25)" : "none",
                  opacity: isActive ? 1 : 0.85,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    background: isActive
                      ? presetGradients[preset].hover
                      : "rgba(0,0,0,0.03)",
                    color: isActive ? "#fff" : presetTextColors[preset],
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {presetLabels[preset]}
              </Button>
            );
          })}
        </Stack>
      </Stack>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        {selectedIds.length > 0 && (
          <FormControlLabel
            control={
              <Switch
                checked={showUnselected}
                onChange={(event) => setShowUnselected(event.target.checked)}
              />
            }
            label={
              t("autoFillModal.showUnselected")
            }
          />
        )}
        {/* Sort by difficulty dropdown 
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">
            {t("autoFillModal.sortDifficulty")}
          </Typography>
          <Select
            size="small"
            value={difficultySort}
            onChange={(event) =>
              setDifficultySort(event.target.value as DifficultySort)
            }
          >
            <MenuItem value="none">
              {t("autoFillModal.sortDifficultyNone")}
            </MenuItem>
            <MenuItem value="asc">
              {t("autoFillModal.sortDifficultyAsc")}
            </MenuItem>
            <MenuItem value="desc">
              {t("autoFillModal.sortDifficultyDesc")}
            </MenuItem>
          </Select>
        </Stack>
        </Stack>*/}
      </Stack>
      {selectionWarning && (
        <Alert severity="warning" onClose={() => setSelectionWarning(null)}>
          {selectionWarning}
        </Alert>
      )}
      {isLessThanMaxSelection && !lessThan15Dismissed && (
        <Typography variant="body2" color="text.secondary">
          {t("autoFillModal.lessThan15Warning")}
        </Typography>
      )}
      {isMaxSelection && (
        <Typography
          variant="body2"
          sx={{
            fontStyle: "italic",
            color: "text.secondary",
            px: 0.5,
          }}
        >
          {t("autoFillModal.autoDeadlineInfo")}
        </Typography>
      )}
      {championsCount > 0 && (
        <Alert severity="info">
          {t("autoFillModal.championsInfo", {
            count: championsCount,
          })}
        </Alert>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ px: 1.5 }}>
                {/* {t("autoFillModal.table.select") || "Select"} */}
              </TableCell>
              {/* <TableCell>
                {t("autoFillModal.table.league")}
              </TableCell> */}
              {/* <TableCell>{t("autoFillModal.table.date") || "Date"}</TableCell> */}
              <TableCell>{t("autoFillModal.table.match")}</TableCell>
              <TableCell>{t("autoFillModal.table.info")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedMatches.map((match) => {
              const isSelected = selectedIds.includes(match.matchId);
              const formattedDate = dayjs(match.startTime).format(
                "DD/MM HH:mm"
              );
              const leagueStyle = getLeagueChipStyle(match.leagueId);
              return (
                <TableRow
                  key={match.matchId}
                  selected={isSelected}
                  hover
                  sx={{
                    opacity: showUnselected || isSelected ? 1 : 0.5,
                  }}
                >
                  <TableCell padding="checkbox" sx={{ px: 1.5 }}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => handleToggle(match.matchId)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="column"
                      spacing={0.5}
                      sx={{ maxWidth: 200, margin: "0 auto" }}
                    >
                      <Chip
                        label={match.homeTeam}
                        size="small"
                        sx={{
                          background: leagueStyle.background,
                          color: leagueStyle.color,
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="body2" sx={{ textAlign: "center" }}>
                        vs
                      </Typography>
                      <Chip
                        label={match.awayTeam}
                        size="small"
                        sx={{
                          background: leagueStyle.background,
                          color: leagueStyle.color,
                          fontWeight: 600,
                        }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Stack direction="column" spacing={0.5}>
                      <LeagueChip leagueId={match.leagueId} size="small" />
                      <Chip
                        label={formattedDate}
                        size="small"
                        sx={{
                          fontWeight: 600,
                        }}
                      />
                      {renderDifficultyChip(match)}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );

  const renderPlenoStep = () => (
    <Stack spacing={2}>
      <Typography>
        {t("autoFillModal.plenoInstructions")}
      </Typography>
      {selectionWarning && <Alert severity="warning">{selectionWarning}</Alert>}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ px: 1.5 }}></TableCell>
              <TableCell>{t("autoFillModal.table.match")}</TableCell>
              <TableCell>{t("autoFillModal.table.info")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedOnly.map((match) => {
              const formattedDate = dayjs(match.startTime).format(
                "DD/MM HH:mm"
              );
              const isPleno = plenoMatchId === match.matchId;
              const leagueStyle = getLeagueChipStyle(match.leagueId);
              return (
                <TableRow key={match.matchId} selected={isPleno} hover>
                  <TableCell padding="checkbox" sx={{ px: 1.5 }}>
                    <Radio
                      size="small"
                      checked={isPleno}
                      onChange={() => setPlenoMatchId(match.matchId)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="column"
                      spacing={0.5}
                      sx={{ maxWidth: 200, margin: "0 auto" }}
                    >
                      <Chip
                        label={match.homeTeam}
                        size="small"
                        sx={{
                          background: leagueStyle.background,
                          color: leagueStyle.color,
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="body2" sx={{ textAlign: "center" }}>
                        vs
                      </Typography>
                      <Chip
                        label={match.awayTeam}
                        size="small"
                        sx={{
                          background: leagueStyle.background,
                          color: leagueStyle.color,
                          fontWeight: 600,
                        }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Stack direction="column" spacing={0.5}>
                      <LeagueChip leagueId={match.leagueId} size="small" />
                      <Chip
                        label={formattedDate}
                        size="small"
                        sx={{
                          fontWeight: 600,
                        }}
                      />
                      {renderDifficultyChip(match)}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );

  const title =
    currentStep === "selection"
      ? t("autoFillModal.stepSelectionTitle")
      : t("autoFillModal.stepPlenoTitle");

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xl"
      onClose={onClose}
      data-testid="auto-fill-modal"
      sx={{
        xs: { width: "100%" },
      }}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 16px)", sm: "auto" },
          margin: { xs: "8px", sm: "0" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "linear-gradient(120deg, #0d47a1, #5e35b1)",
          color: "#fff",
          borderBottom: "3px solid rgba(255,255,255,0.25)",
          boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            flexGrow: 1,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          {title}
        </Typography>
        <Tooltip
          title={
            t("autoFillModal.helperText")
          }
        >
          <IconButton size="small" sx={{ color: "#3e2723" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ minHeight: 400, px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
      >
        {loading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight={300}
            flexDirection="column"
            gap={2}
          >
            <CircularProgress />
            <Typography variant="body2">
              {t("autoFillModal.loadingMatches")}
            </Typography>
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && data && (
          <>
            {currentStep === "selection"
              ? renderSelectionStep()
              : renderPlenoStep()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={secondaryActionStyles}>
          {t("autoFillModal.cancel")}
        </Button>
        {currentStep === "selection" && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={selectedIds.length === 0 || !data}
            sx={primaryActionStyles}
          >
            {t("autoFillModal.next")}
          </Button>
        )}
        {currentStep === "pleno" && (
          <>
            <Button
              onClick={() => setCurrentStep("selection")}
              sx={secondaryActionStyles}
            >
              {t("autoFillModal.back")}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              sx={primaryActionStyles}
            >
              {t("autoFillModal.apply")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
